// =============================================================================
// Shared utilities — used by web app, extension, and Cloud Functions
// =============================================================================

import type {
  Scholarship,
  QueueWeights,
  WordCountBucket,
} from '@scholarship-app/types';

// -----------------------------------------------------------------------------
// Effective deadline computation
// -----------------------------------------------------------------------------
// For scholarships tied to admission deadlines (e.g., Park requires NC State
// app by Nov 1 even though the scholarship's own deadline is Dec 1), the
// effective deadline is the earlier of the two.

export function computeEffectiveDeadline(
  scholarship: Pick<Scholarship, 'deadline' | 'linked_admission_deadline'>
): string | null {
  const a = scholarship.deadline;
  const b = scholarship.linked_admission_deadline;
  if (!a && !b) return null;
  if (!a) return b;
  if (!b) return a;
  return a < b ? a : b;
}

// -----------------------------------------------------------------------------
// Priority scoring (subset of full algorithm; full version in Phase 3)
// -----------------------------------------------------------------------------

export function deadlineUrgencyScore(effectiveDeadline: string | null, today: Date = new Date()): number {
  if (!effectiveDeadline) return 0;
  const deadline = new Date(effectiveDeadline);
  const daysUntil = Math.ceil(
    (deadline.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );
  if (daysUntil < 0) return 0; // past deadline
  if (daysUntil <= 7) return 100;
  if (daysUntil <= 14) return 85;
  if (daysUntil <= 30) return 70;
  if (daysUntil <= 60) return 50;
  if (daysUntil <= 120) return 30;
  return 10;
}

export function normalizedAmountScore(
  amountMin: number | null,
  amountMax: number | null,
  amountType: string
): number {
  if (amountType === 'full_ride') return 100;
  const amount = amountMax ?? amountMin ?? 0;
  if (amount >= 50000) return 100;
  if (amount >= 20000) return 85;
  if (amount >= 10000) return 70;
  if (amount >= 5000) return 55;
  if (amount >= 2000) return 40;
  if (amount >= 1000) return 25;
  if (amount > 0) return 15;
  return 0;
}

export function effortBonusScore(effort: 'S' | 'M' | 'L'): number {
  switch (effort) {
    case 'S':
      return 100;
    case 'M':
      return 50;
    case 'L':
      return 20;
  }
}

export function tierBoost(
  scholarship: Pick<Scholarship, 'school_tier_at_creation'>,
  weights: QueueWeights
): number {
  if (scholarship.school_tier_at_creation === 'primary') {
    return weights.primary_tier_boost_points;
  }
  if (scholarship.school_tier_at_creation === 'backup') {
    return weights.backup_tier_boost_points;
  }
  return 0;
}

export function computePriorityScore(
  scholarship: Scholarship,
  weights: QueueWeights,
  today: Date = new Date()
): number {
  const eff = computeEffectiveDeadline(scholarship);
  const deadlineScore = deadlineUrgencyScore(eff, today);
  const amountScore = normalizedAmountScore(
    scholarship.amount_min,
    scholarship.amount_max,
    scholarship.amount_type
  );
  const fitScore = scholarship.fit_score ?? 0;
  const effortScore = effortBonusScore(scholarship.effort_estimate);

  return (
    deadlineScore * weights.w_deadline +
    amountScore * weights.w_amount +
    fitScore * weights.w_fit +
    effortScore * weights.w_effort +
    tierBoost(scholarship, weights)
  );
}

// -----------------------------------------------------------------------------
// Word count bucket helpers
// -----------------------------------------------------------------------------

export function bucketForWordCount(count: number): WordCountBucket {
  if (count <= 275) return '250';
  if (count <= 600) return '500';
  if (count <= 1100) return '1000';
  return 'unlimited';
}

export function defaultBuckets(): WordCountBucket[] {
  return ['250', '500', '1000', 'unlimited'];
}

// -----------------------------------------------------------------------------
// Fingerprint helper for inbox dedup
// -----------------------------------------------------------------------------

export function inboxItemFingerprint(name: string, sponsor: string, year?: number): string {
  const slug = (s: string) =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  return year ? `${slug(name)}-${slug(sponsor)}-${year}` : `${slug(name)}-${slug(sponsor)}`;
}

// Profile field flattening for autofill (v0.2.39)
export {
  profileToFillableFields,
  groupFillableFields,
  type FillableField,
  type FillGroup,
} from './profileFields';
